import React, { useMemo, useState } from 'react';
import {
  CheckCircle2,
  Circle,
  ClipboardList,
  Loader2,
  MessageSquareText,
  PenLine,
  Trophy,
  X,
} from 'lucide-react';
import { submitAssignment } from '../../services/assignmentService';
import { cardTheme } from '../../styles/cardTheme';
import {
  AssignmentForStudent,
  AssignmentSubmissionResult,
  formatAssignmentOptionLabel,
  getTrueFalseOptions,
  normalizeAssignmentQuestionType,
  normalizeSubmissionStatus,
} from '../../types/assignment';

interface AssignmentContentProps {
  assignment: AssignmentForStudent;
  onComplete: () => void;
}

export const AssignmentContent: React.FC<AssignmentContentProps> = ({
  assignment,
  onComplete,
}) => {
  const sortedQuestions = useMemo(
    () => [...assignment.questions].sort((a, b) => a.order - b.order),
    [assignment.questions]
  );

  const initialAnswers = useMemo(() => {
    const map: Record<string, string> = {};
    sortedQuestions.forEach((q) => {
      if (q.answerText != null && q.answerText !== '') {
        map[q.id] = q.answerText;
      }
    });
    return map;
  }, [sortedQuestions]);

  const [answers, setAnswers] = useState<Record<string, string>>(initialAnswers);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submissionResult, setSubmissionResult] =
    useState<AssignmentSubmissionResult | null>(null);

  const alreadySubmitted = assignment.hasSubmitted || !!submissionResult;
  const submission = submissionResult
    ? {
        totalGrade: submissionResult.totalGrade,
        maxGrade: submissionResult.maxGrade ?? assignment.totalGrade,
        feedback: submissionResult.feedback,
        status: normalizeSubmissionStatus(submissionResult.status),
        submittedAt: submissionResult.submittedAt,
      }
    : assignment.submission
      ? {
          totalGrade: assignment.submission.totalGrade,
          maxGrade: assignment.totalGrade,
          feedback: assignment.submission.feedback,
          status: normalizeSubmissionStatus(assignment.submission.status),
          submittedAt: assignment.submission.submittedAt,
        }
      : null;

  const setAnswer = (questionId: string, value: string) => {
    if (alreadySubmitted) return;
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const allAnswered = sortedQuestions.every((q) => {
    const value = answers[q.id];
    return value != null && value.trim() !== '';
  });

  const handleSubmit = async () => {
    if (!allAnswered || alreadySubmitted) return;

    setSubmitting(true);
    setError(null);

    try {
      const result = await submitAssignment({
        assignmentId: assignment.id,
        answers: sortedQuestions.map((q) => ({
          questionId: q.id,
          answerText: answers[q.id]?.trim() ?? '',
        })),
      });
      setSubmissionResult(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'فشل في إرسال الواجب';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinish = () => {
    onComplete();
  };

  const dueLabel = assignment.dueDate
    ? new Date(assignment.dueDate).toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null;

  return (
    <div className="w-full max-w-full min-w-0 overflow-x-hidden font-cairo" dir="rtl">
      <div className="w-full max-w-full min-w-0 space-y-4 sm:space-y-5">
        <div className={`${cardTheme.shell} overflow-hidden`}>
          <div className={`${cardTheme.headerBanner} px-4 py-4 sm:px-5 sm:py-5`}>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex shrink-0 ${cardTheme.badgeGold} border-gold-400/40 bg-gold-500/20 text-gold-200`}
              >
                <ClipboardList className="h-3.5 w-3.5" />
                واجب
              </span>
              {assignment.totalGrade != null && (
                <span className="text-xs font-semibold text-green-100 sm:text-sm">
                  الدرجة الكلية: {assignment.totalGrade}
                </span>
              )}
              {dueLabel && (
                <span className="text-xs text-green-100/80 sm:text-sm">
                  التسليم قبل: {dueLabel}
                </span>
              )}
            </div>
            <h3 className="min-w-0 break-words text-base font-bold text-white sm:text-lg">
              {assignment.lessonName || 'الواجب'}
            </h3>
            <p className="mt-1 text-sm text-green-100/90">
              {alreadySubmitted
                ? 'تم إرسال إجاباتك — يمكنك مراجعة التسليم أدناه'
                : 'أجب عن جميع الأسئلة ثم أرسل الواجب'}
            </p>
          </div>
        </div>

        {alreadySubmitted && submission && (
          <div className={`${cardTheme.shell} ${cardTheme.accentTop} p-4 sm:p-5`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${cardTheme.iconBoxGold}`}
                >
                  {submission.status === 'Graded' ? (
                    <Trophy className="h-5 w-5" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className={`text-sm font-bold sm:text-base ${cardTheme.title}`}>
                    {submission.status === 'Graded'
                      ? 'تم تصحيح الواجب'
                      : 'تم إرسال الواجب'}
                  </p>
                  <p className={`mt-0.5 text-xs sm:text-sm ${cardTheme.subtitle}`}>
                    {submission.status === 'Graded'
                      ? 'ظهرت درجتك بعد التصحيح'
                      : 'بانتظار تصحيح المعلم للأسئلة المقالية إن وُجدت'}
                  </p>
                </div>
              </div>
              {submission.totalGrade != null && (
                <div className="text-right">
                  <p className={`text-2xl font-bold sm:text-3xl ${cardTheme.price}`}>
                    {submission.totalGrade}
                    {submission.maxGrade != null ? (
                      <span className="text-base font-semibold text-gray-500">
                        /{submission.maxGrade}
                      </span>
                    ) : null}
                  </p>
                </div>
              )}
            </div>
            {submission.feedback && (
              <div className="mt-3 rounded-xl border border-green-100 bg-green-50/60 p-3 text-sm text-green-900">
                <div className="mb-1 flex items-center gap-1.5 font-semibold">
                  <MessageSquareText className="h-4 w-4" />
                  ملاحظات المعلم
                </div>
                <p className="leading-relaxed">{submission.feedback}</p>
              </div>
            )}
          </div>
        )}

        <div className="space-y-3 sm:space-y-4">
          {sortedQuestions.map((question, index) => {
            const type = normalizeAssignmentQuestionType(question.type);
            const value = answers[question.id] ?? '';
            const choiceOptions =
              type === 'TrueFalse'
                ? getTrueFalseOptions(question.options)
                : question.options ?? [];

            return (
              <div
                key={question.id}
                className={`${cardTheme.shell} ${cardTheme.accentTop} w-full max-w-full min-w-0 overflow-hidden p-4 sm:p-5`}
              >
                <div className="mb-3 flex min-w-0 items-start gap-2.5">
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-green-700 text-xs font-bold text-white">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p
                      className={`break-words text-sm font-bold leading-relaxed sm:text-base ${cardTheme.title}`}
                    >
                      {question.text}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <span className={cardTheme.badge}>
                        {type === 'OpenQuestion' && (
                          <>
                            <PenLine className="h-3.5 w-3.5" />
                            سؤال مقالي
                          </>
                        )}
                        {type === 'SingleChoice' && 'اختيار من متعدد'}
                        {type === 'TrueFalse' && 'صح وخطأ'}
                      </span>
                      {question.maxGrade != null && (
                        <span className="text-xs text-gray-500">
                          ({question.maxGrade} درجة)
                        </span>
                      )}
                      {alreadySubmitted && question.grade != null && (
                        <span className={cardTheme.badgeGold}>
                          درجتك: {question.grade}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {type === 'OpenQuestion' ? (
                  <textarea
                    value={value}
                    onChange={(e) => setAnswer(question.id, e.target.value)}
                    disabled={alreadySubmitted}
                    rows={4}
                    placeholder="اكتب إجابتك هنا..."
                    className="w-full min-h-[7rem] resize-y rounded-xl border-2 border-green-100 bg-white p-3 text-sm leading-relaxed text-gray-800 outline-none transition focus:border-gold-400 disabled:bg-gray-50 disabled:text-gray-700 sm:text-base"
                  />
                ) : (
                  <div className="space-y-2.5">
                    {choiceOptions.map((option) => {
                      const selected = value === option;
                      return (
                        <button
                          key={option}
                          type="button"
                          disabled={alreadySubmitted}
                          onClick={() => setAnswer(question.id, option)}
                          className={`flex w-full min-h-12 min-w-0 items-center gap-3 rounded-xl border-2 p-3.5 text-right transition-all sm:min-h-14 sm:p-4 disabled:cursor-default ${
                            selected
                              ? 'border-gold-400 bg-gold-50 shadow-sm shadow-gold-500/20'
                              : 'border-green-100 bg-white hover:border-green-300 hover:bg-green-50/40 disabled:hover:border-green-100 disabled:hover:bg-white'
                          }`}
                        >
                          <span
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold sm:h-9 sm:w-9 ${
                              selected
                                ? cardTheme.iconBoxGold
                                : 'bg-green-50 text-green-800'
                            }`}
                          >
                            {selected ? (
                              <CheckCircle2 className="h-4 w-4" />
                            ) : (
                              <Circle className="h-4 w-4 text-gray-300" />
                            )}
                          </span>
                          <span
                            className={`min-w-0 flex-1 break-words text-sm font-medium leading-snug sm:text-base ${
                              selected ? 'text-green-950' : 'text-gray-800'
                            }`}
                          >
                            {formatAssignmentOptionLabel(option, type)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {alreadySubmitted && question.feedback && (
                  <div className="mt-3 rounded-lg border border-gold-200 bg-gold-50/70 px-3 py-2 text-sm text-green-900">
                    <span className="font-semibold">ملاحظة: </span>
                    {question.feedback}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
            <X className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1">{error}</span>
          </div>
        )}

        {!alreadySubmitted ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!allAnswered || submitting}
            className={`flex w-full min-h-12 items-center justify-center gap-2 rounded-2xl px-5 text-sm font-bold sm:min-h-14 sm:text-base disabled:cursor-not-allowed disabled:opacity-50 ${cardTheme.btnPrimary}`}
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                جاري الإرسال...
              </>
            ) : (
              <>
                <ClipboardList className="h-5 w-5" />
                إرسال الواجب
              </>
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleFinish}
            className={`flex w-full min-h-12 items-center justify-center gap-2 rounded-2xl px-5 text-sm font-bold sm:min-h-14 sm:text-base ${cardTheme.btnGold}`}
          >
            إنهاء
          </button>
        )}
      </div>
    </div>
  );
};
